import { authenticationContext } from '@codametrix/ui-common';
import { AtRiskTier, ManagerHomeProject, ManagerHomeSection } from './types';

export const sortProjects = (rows: ManagerHomeProject[]): ManagerHomeProject[] =>
  [...rows].sort((a, b) => {
    if (a.endDate === null && b.endDate === null) {
      return a.createdAt.localeCompare(b.createdAt);
    }
    if (a.endDate === null) return 1;
    if (b.endDate === null) return -1;
    const cmp = a.endDate.localeCompare(b.endDate);
    if (cmp !== 0) return cmp;
    return a.createdAt.localeCompare(b.createdAt);
  });

export const applyGroupFilter = (
  rows: ManagerHomeProject[],
  selectedGroupIds: Set<string>,
): ManagerHomeProject[] => rows.filter((row) => selectedGroupIds.has(row.groupId));

export const applyNameFilter = (
  rows: ManagerHomeProject[],
  term: string,
): ManagerHomeProject[] => {
  if (term.length < 2) return rows;
  const lower = term.toLowerCase();
  return rows.filter((row) => row.name.toLowerCase().includes(lower));
};

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const formatDueDate = (isoDate: string | null): string => {
  if (!isoDate) return 'No due date';
  const dateOnly = isoDate.slice(0, 10);
  const [, monthStr, dayStr] = dateOnly.split('-');
  const month = MONTH_NAMES[parseInt(monthStr, 10) - 1];
  const day = parseInt(dayStr, 10);
  return `Due ${month} ${day}`;
};

export const formatProgressStat = (
  section: ManagerHomeSection,
  completeCount: number | null,
  approvedCount: number | null,
  totalCount: number | null,
): string | null => {
  if (section === 'needs_assignment') return null;
  if (section === 'coder_complete') {
    if (completeCount === null) return null;
    return `${completeCount} awaiting audit`;
  }
  // in_flight
  if (completeCount === null || approvedCount === null || totalCount === null) return null;
  const done = completeCount + approvedCount;
  if (totalCount === 0) return '0 / 0';
  const pct = Math.round((done / totalCount) * 100);
  return `${done} / ${totalCount} (${pct}%)`;
};

export const deriveAtRiskTier = (endDate: string | null, now: Date = new Date()): AtRiskTier => {
  if (!endDate) return 'none';
  const end = new Date(endDate.slice(0, 10) + 'T00:00:00');
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = end.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / 86_400_000);
  if (diffDays < 0) return 'overdue';
  if (diffDays <= 3) return 'due-soon';
  return 'none';
};

export const isStalled = (lastActivityAt: string | null, now: Date = new Date()): boolean => {
  if (!lastActivityAt) return false;
  const cursor = new Date(lastActivityAt);
  cursor.setHours(0, 0, 0, 0);
  cursor.setDate(cursor.getDate() + 1);
  const end = new Date(now);
  end.setHours(0, 0, 0, 0);
  let count = 0;
  while (cursor <= end) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) count++;
    if (count >= 5) return true;
    cursor.setDate(cursor.getDate() + 1);
  }
  return false;
};

/** Days between today and endDate. Positive = days remaining, negative = days past due. */
export const deriveDaysFromEnd = (
  endDate: string | null,
  now: Date = new Date(),
): number | null => {
  if (!endDate) return null;
  const end = new Date(endDate.slice(0, 10) + 'T00:00:00');
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((end.getTime() - today.getTime()) / 86_400_000);
};

/** Calendar days since lastActivityAt (for the "Stalled Nd" badge label). */
export const stalledCalendarDays = (
  lastActivityAt: string | null,
  now: Date = new Date(),
): number | null => {
  if (!lastActivityAt) return null;
  const last = new Date(lastActivityAt);
  last.setHours(0, 0, 0, 0);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((today.getTime() - last.getTime()) / 86_400_000));
};

/** Bearer token for direct cqa-service fetch calls (same source as qa-apollo.client). */
export const getAuthHeader = (): string => {
  const token = authenticationContext.getToken();
  if (!token) return '';
  return token.startsWith('Bearer') ? token : `Bearer ${token}`;
};
